import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { OptimizedImage } from '@/components/OptimizedImage';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEOHead';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { ArrowRight, Target, Camera, Star, MapPin, Moon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useHydrated } from '@/hooks/use-hydrated';
import { getGuideBySlug } from '@/lib/locationGuides';

const MarkdownContent: React.FC<{ content: string }> = ({ content }) => (
  <div className="prose prose-lg prose-slate dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-code:text-foreground prose-blockquote:text-muted-foreground prose-img:rounded-lg prose-img:shadow-lg prose-figcaption:text-center prose-figcaption:italic prose-figcaption:text-muted-foreground prose-figcaption:text-sm prose-figcaption:mt-2">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        // The page already renders the guide title as the h1. A markdown
        // "# Title" would be a second one, which Ahrefs counts as multiple H1s.
        h1: ({ children }) => <h2>{children}</h2>,
        img: ({ src, alt, ...props }) =>
          src ? (
            <OptimizedImage
              src={src}
              alt={alt || ''}
              width={1080}
              quality={80}
              className="w-full rounded-lg shadow-lg"
              {...props}
            />
          ) : null,
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);

const LocationGuidePage: React.FC = () => {
  const { user, isSubscribed, loading } = useAuth();
  const hydrated = useHydrated();
  const showSubscriberCTA = hydrated && !loading && !!user && isSubscribed;

  // Derive slug from the current URL path (flat routes, no :slug param)
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, '').replace(/\/$/, '');
  const guide = getGuideBySlug(slug);

  if (!guide) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="max-w-3xl mx-auto mt-8 text-center">
            <h1 className="text-3xl font-bold mb-4 text-foreground">Guide Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The location guide you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/blog">
              <Button variant="outline">Back to Blog</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Build planner URL from frontmatter coordinates
  const plannerUrl = guide.planner_lat && guide.planner_lon && guide.planner_name
    ? `/planner?name=${encodeURIComponent(guide.planner_name)}&lat=${guide.planner_lat}&lon=${guide.planner_lon}`
    : '/planner';

  // Extract state name from title (e.g. "Best Milky Way Locations in Colorado" → "Colorado")
  const stateMatch = guide.title.match(/in\s+(.+)$/i);
  const stateName = stateMatch ? stateMatch[1] : '';

  // Split content at --- separators to inject mid-content upsell after ~5 locations
  // Structure: intro | loc1 | loc2 | ... | loc10 | closing section
  // We append the separator back to firstHalf so the <hr> still renders before the upsell card.
  const sections = guide.content.split(/\n\n---\n\n/);
  const midPoint = Math.ceil(sections.length / 2);
  const firstHalf = sections.slice(0, midPoint).join('\n\n---\n\n') + '\n\n---\n\n';
  const secondHalf = sections.slice(midPoint).join('\n\n---\n\n');

  const webPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": guide.seo_title || guide.title,
    "description": guide.seo_description || guide.description,
    "url": `https://www.milkywayplanner.com/${guide.slug}`,
    "publisher": {
      "@type": "Organization",
      "name": "Milky Way Planner",
      "url": "https://www.milkywayplanner.com"
    }
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.milkywayplanner.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": guide.title,
        "item": `https://www.milkywayplanner.com/${guide.slug}`
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={guide.seo_title || guide.title}
        description={guide.seo_description || guide.description}
        keywords={guide.seo_keywords || undefined}
        canonicalUrl={`/${guide.slug}`}
        structuredData={[webPageStructuredData, breadcrumbStructuredData]}
      />

      {/* Dark hero banner */}
      <div className="bg-[#0a1628] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-gold uppercase tracking-wider">Dark Sky Location Guide</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {guide.title}
            </h1>
            {guide.description && (
              <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
                {guide.description}
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              {!showSubscriberCTA && (
                <Link to="/register">
                  <Button variant="premium">
                    <Star className="w-4 h-4 mr-2" />
                    Get Premium — $25/yr
                  </Button>
                </Link>
              )}
              <Link to={plannerUrl}>
                <Button variant="ghost" className="border border-white/30 text-white hover:bg-white/10 hover:text-white hover:border-white/50">
                  <Target className="w-4 h-4 mr-2" />
                  Plan a Shoot in {stateName}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="max-w-3xl mx-auto">

            <article>
              {/* First half of content */}
              <MarkdownContent content={firstHalf} />

              {/* Mid-content upsell — always visible, content varies by subscription */}
              {showSubscriberCTA ? (
                <div className="my-10 rounded-xl bg-[#0f1923] border border-white/10 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-0.5">
                      <MapPin className="w-6 h-6 text-gold" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white text-lg mb-1">
                        Check timing for any of these locations
                      </h3>
                      <p className="text-white/60 text-sm mb-4">
                        Open the planner for exact galactic core rise times, moon phases, and nightly visibility ratings for any location in {stateName}.
                      </p>
                      <Link to={plannerUrl}>
                        <Button className="bg-gold hover:bg-gold-dark text-black font-semibold" size="sm">
                          Open Planner
                          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="my-10 rounded-xl bg-[#0f1923] border border-white/10 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-0.5">
                      <Moon className="w-6 h-6 text-gold" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white text-lg mb-1">
                        Know exactly when to shoot — before you drive out there
                      </h3>
                      <p className="text-white/60 text-sm mb-4">
                        Premium gives you nightly visibility ratings, moon phase overlays, a Moon Planner, Trip Planner, and PDF exports for any location on this list.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <Link to="/register">
                          <Button variant="premium" size="sm">
                            Go Premium — $25/year
                            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                          </Button>
                        </Link>
                        <Link to={plannerUrl}>
                          <Button variant="ghost" size="sm" className="border border-white/30 text-white hover:bg-white/10 hover:text-white hover:border-white/50">
                            Try the free planner
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <hr className="border-border mt-0 mb-8" />

              {/* Second half of content */}
              <MarkdownContent content={secondHalf} />
            </article>

            {/* Location-specific planner CTA */}
            {stateName && (
              <div className="mt-12 p-6 rounded-xl bg-[#0f1923] border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <MapPin className="w-6 h-6 text-gold" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white text-lg mb-1">
                      Plan your {stateName} shoot
                    </h3>
                    <p className="text-white/60 text-sm mb-4">
                      Check galactic core rise times, moon phases, and nightly visibility ratings for any location in {stateName}.
                    </p>
                    <Link to={plannerUrl}>
                      <Button className="bg-gold hover:bg-gold-dark text-black font-semibold">
                        Open Planner
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Standard 3-up CTAs */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link to="/planner" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-gold" />
                  <h4 className="font-semibold text-white">Plan Your Shoot</h4>
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-3">
                  Free Milky Way calendar with nightly visibility ratings.
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                  Open Planner <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              <Link to="/workshops" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Camera className="w-5 h-5 text-gold" />
                  <h4 className="font-semibold text-white">Browse Workshops</h4>
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-3">
                  Astrophotography workshops at dark sky locations.
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                  Find a Workshop <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              {!showSubscriberCTA ? (
                <Link to="/register" className="group p-6 rounded-xl bg-gold/10 border border-gold/20 hover:bg-gold/15 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-5 h-5 text-gold" />
                    <h4 className="font-semibold text-foreground">Go Premium</h4>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    Moon Planner, Trip Planner, exports — $25/year.
                  </p>
                  <span className="text-sm font-semibold text-gold group-hover:text-gold-dark transition-colors inline-flex items-center gap-1">
                    Start Premium <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              ) : (
                <Link to="/workshops/instructors" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-5 h-5 text-gold" />
                    <h4 className="font-semibold text-white">Meet Instructors</h4>
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed mb-3">
                    Browse instructor profiles and find your next workshop.
                  </p>
                  <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                    View Instructors <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Newsletter — full width, flows into footer */}
      <NewsletterSignup variant="blog" />
    </>
  );
};

export default LocationGuidePage;
